import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import useAiQuota from '@/hooks/useAiQuota';
import {
  User, MapPin, Calendar, Stethoscope, BookOpen, ExternalLink,
  Plus, Trash2, Save, Loader2, CheckCircle, XCircle, Clock, AlertCircle, ToggleRight, Star, Sparkles,
  Camera, Upload, Wallet, UserX, Crown, CreditCard, Download, Lock, AlertTriangle, Copy, Share2, MessageCircle,
  FileText, Image, Send, SpellCheck, Bell, BellOff, BarChart3, TrendingUp, Eye, EyeOff, Phone, Search,
  Newspaper, History, Check, X, Edit3, RefreshCw, ChevronDown, QrCode, Zap, Gift, Ticket, ClipboardList
} from 'lucide-react';
import BookingChat from '@/components/BookingChat';
import PinSettings from '@/components/dashboard/PinSettings';
import ProfilePublishedCelebration from '@/components/dashboard/ProfilePublishedCelebration';
import MonogramAvatar from '@/components/MonogramAvatar';
import VoiceMeter from '@/components/dashboard/VoiceMeter';
import ClinicSearchPicker from '@/components/ClinicSearchPicker';
import ClinicMap from '@/components/ClinicMap';
import HospitalAttachmentsSection from '@/components/dashboard/HospitalAttachmentsSection';
import {
  Field, ShareUrlBar, PushNotificationToggle, RedeemCouponCard,
  VoiceAuthenticityNotice, ChangePasswordSection,
  WhatsNewLaunchBanner, SetupBlocker, WebsiteScoreWidget,
  TierBadgeWithUpgrade, UpgradeBanner, SubscriptionWarning,
} from '@/components/dashboard/widgets/SharedWidgets';

export default function ClinicBrandingTab() {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ clinic_display_name: '', clinic_display_address: '', clinic_display_pincode: '', clinic_map_enabled: true });
  const [narrativeForm, setNarrativeForm] = useState({ why_independent: '', clinic_announcement: '', announcement_expires_hours: 48 });
  const [saving, setSaving] = useState(false);
  const [savingNarrative, setSavingNarrative] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [narrativeMsg, setNarrativeMsg] = useState({ type: '', text: '' });
  // QR Save-contact phone-share toggle (Premium+). Privacy-first default
  // (False) so doctors' chat/voice modules keep their purpose. UI lives
  // below — the row is rendered for all tiers, but locked for free.
  const [savingQr, setSavingQr] = useState(false);
  const [qrMsg, setQrMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    api.get('/dashboard/branding').then(r => {
      setState(r.data);
      setForm({
        clinic_display_name: r.data.clinic_display_name || '',
        clinic_display_address: r.data.clinic_display_address || '',
        clinic_display_pincode: r.data.clinic_display_pincode || '',
        clinic_map_enabled: r.data.clinic_map_enabled !== false,
      });
      setNarrativeForm({
        why_independent: r.data.why_independent || '',
        clinic_announcement: r.data.clinic_announcement || '',
        announcement_expires_hours: 48,
      });
    }).catch(e => setMsg({ type: 'error', text: formatApiError(e) }));
  }, []);

  const saveNarrative = async () => {
    setSavingNarrative(true); setNarrativeMsg({ type: '', text: '' });
    try {
      const r = await api.put('/dashboard/branding/narrative', narrativeForm);
      setNarrativeMsg({ type: 'success', text: 'Narrative saved. Live on your public website.' });
      // Refresh state announcement/why fields so preview below reflects saved state
      setState(s => ({ ...s, why_independent: r.data.why_independent, clinic_announcement: r.data.clinic_announcement, announcement_expires_at: r.data.announcement_expires_at }));
    } catch (e) {
      setNarrativeMsg({ type: 'error', text: formatApiError(e) });
    } finally {
      setSavingNarrative(false);
    }
  };

  const save = async () => {
    setSaving(true); setMsg({ type: '', text: '' });
    try {
      const r = await api.put('/dashboard/branding/clinic-info', form);
      setState(r.data);
      setMsg({ type: 'success', text: 'Saved. Your public website reflects this immediately.' });
    } catch (e) {
      setMsg({ type: 'error', text: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Remove clinic branding from your public website?')) return;
    setSaving(true);
    try {
      await api.delete('/dashboard/branding/clinic-info');
      setForm({ clinic_display_name: '', clinic_display_address: '', clinic_display_pincode: '', clinic_map_enabled: true });
      setState({ ...state, clinic_display_name: '', clinic_display_address: '', clinic_display_pincode: '' });
      setMsg({ type: 'success', text: 'Cleared. Monogram remains (automatic).' });
    } catch (e) {
      setMsg({ type: 'error', text: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const toggleQrSharePhone = async (next) => {
    setSavingQr(true); setQrMsg({ type: '', text: '' });
    try {
      const r = await api.put('/dashboard/branding/qr-settings', { share_phone: !!next });
      setState(s => ({ ...s, qr_share_phone: r.data.qr_share_phone }));
      setQrMsg({
        type: 'success',
        text: r.data.qr_share_phone
          ? 'Saved. Your phone number will now be embedded in the Save-contact QR. New QRs regenerate automatically.'
          : 'Saved. Your phone stays private — patients reach you only via in-app Chat & Voice Call.',
      });
      // Auto-clear the toast so it doesn't linger
      setTimeout(() => setQrMsg({ type: '', text: '' }), 5000);
    } catch (e) {
      setQrMsg({ type: 'error', text: formatApiError(e) });
    } finally {
      setSavingQr(false);
    }
  };

  if (!state) return <div className="text-slate-500 text-sm" data-testid="branding-loading">Loading branding…</div>;

  const canEdit = state.can_edit_clinic_info;
  const mg = state.monogram;

  return (
    <div className="space-y-6" data-testid="clinic-branding-tab">
      <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
        <h2 className="text-lg font-medium text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Clinic Branding</h2>
        <p className="text-sm text-slate-500 mb-6">Your monogram is always live. Clinic name, address and map are Premium features.</p>

        {/* MONOGRAM — all tiers */}
        <div className="border border-slate-200 rounded-xl p-5 bg-slate-50/50 mb-5">
          <div className="flex items-center gap-4">
            <MonogramAvatar initials={mg.initials} bgColor={mg.bg_color} size={72} />
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Your Monogram</p>
              <p className="text-sm text-slate-700">Auto-generated from your name + specialty. Appears on your public website.</p>
              <p className="text-xs text-slate-500 mt-1 font-mono">{mg.initials} · {mg.bg_color}</p>
            </div>
          </div>
        </div>

        {/* QR SAVE-CONTACT PRIVACY — Premium+, free tier sees locked row */}
        <div
          className={`border rounded-xl p-5 mb-5 ${state.can_edit_qr_share_phone ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-200 bg-amber-50/40'}`}
          data-testid="qr-share-phone-section"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <QrCode size={14} className={state.can_edit_qr_share_phone ? 'text-emerald-700' : 'text-amber-700'} />
                <p className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Save-contact QR · phone visibility</p>
                {!state.can_edit_qr_share_phone && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">Premium</span>
                )}
              </div>
              <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                By default your Save-contact QR includes only your <b>name, clinic address &amp; website</b> — your phone stays private so patients reach you through in-app Chat &amp; Voice Call. Toggle ON to <b>also</b> embed your mobile number in the QR (one-tap "Save with phone" on the patient's camera).
              </p>
              {!state.can_edit_qr_share_phone && (
                <p className="text-[11px] text-amber-800 mt-2 flex items-center gap-1.5">
                  <Lock size={11} /> Locked on Free. Upgrade to Premium to choose. Your in-app Chat &amp; Voice Call stay available either way.
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <label className={`relative inline-flex items-center ${state.can_edit_qr_share_phone ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                <input
                  type="checkbox"
                  checked={!!state.qr_share_phone}
                  onChange={(e) => state.can_edit_qr_share_phone && toggleQrSharePhone(e.target.checked)}
                  disabled={!state.can_edit_qr_share_phone || savingQr}
                  className="sr-only peer"
                  data-testid="qr-share-phone-toggle"
                />
                <div className="w-11 h-6 bg-slate-300 peer-checked:bg-emerald-500 rounded-full transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
                <span className="ml-3 text-xs font-semibold text-slate-700 min-w-[60px]" data-testid="qr-share-phone-state">
                  {savingQr ? '…' : state.qr_share_phone ? 'Phone ON' : 'Phone OFF'}
                </span>
              </label>
              {!state.can_edit_qr_share_phone && (
                <Link to="/upgrade" className="text-xs font-semibold text-amber-700 underline shrink-0" data-testid="qr-share-upgrade-link">Upgrade</Link>
              )}
            </div>
          </div>
          {qrMsg.text && (
            <div
              className={`text-xs mt-3 p-2 rounded-md ${qrMsg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}
              data-testid="qr-share-phone-msg"
            >{qrMsg.text}</div>
          )}
        </div>

        {/* CLINIC INFO — Premium+ */}
        <div className={`border rounded-xl p-5 ${canEdit ? 'border-sky-200 bg-sky-50/30' : 'border-amber-200 bg-amber-50/50'}`}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Clinic Name, Address & Map</p>
              <p className="text-xs text-slate-500 mt-0.5">{canEdit ? 'Shows on your public website below the hero.' : 'Available on Premium and TurboCharge.'}</p>
            </div>
            {!canEdit && <Link to="/upgrade" className="text-xs font-semibold text-amber-700 underline" data-testid="branding-upgrade-link">Upgrade</Link>}
          </div>

          {canEdit ? (
            <div className="space-y-3 mt-2">
              <div>
                <label className="text-xs font-medium text-slate-700">Clinic Display Name</label>
                <input type="text" maxLength={100} value={form.clinic_display_name}
                  onChange={e => setForm({ ...form, clinic_display_name: e.target.value })}
                  placeholder="e.g. Skyline Heart Clinic"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  data-testid="branding-clinic-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Address</label>
                <textarea rows={2} maxLength={300} value={form.clinic_display_address}
                  onChange={e => setForm({ ...form, clinic_display_address: e.target.value })}
                  placeholder="e.g. Shop 4, Linking Road, Bandra West, Mumbai"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  data-testid="branding-clinic-address" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Pincode</label>
                <input type="text" inputMode="numeric" pattern="[0-9]{4,10}" maxLength={10} value={form.clinic_display_pincode}
                  onChange={e => setForm({ ...form, clinic_display_pincode: e.target.value.replace(/[^0-9]/g, '') })}
                  placeholder="e.g. 400050"
                  className="mt-1 w-40 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  data-testid="branding-clinic-pincode" />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700 pt-1">
                <input type="checkbox" checked={form.clinic_map_enabled}
                  onChange={e => setForm({ ...form, clinic_map_enabled: e.target.checked })}
                  data-testid="branding-map-toggle" />
                Show OpenStreetMap of my clinic location on the public website
              </label>

              {msg.text && (
                <div className={`text-xs p-2 rounded-md ${msg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`} data-testid="branding-msg">{msg.text}</div>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <button onClick={save} disabled={saving}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
                  data-testid="branding-save-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                </button>
                {(state.clinic_display_name || state.clinic_display_address) && (
                  <button onClick={clear} disabled={saving}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
                    data-testid="branding-clear-btn">
                    <Trash2 size={14} /> Clear
                  </button>
                )}
              </div>

              {/* Live map preview — only renders once the pincode is a complete 6-digit
                  Indian PIN, so we don't fire a geocode request on every keystroke
                  (which used to crash the map and blank the screen). */}
              {form.clinic_map_enabled && form.clinic_display_name && form.clinic_display_address && /^\d{6}$/.test(form.clinic_display_pincode || '') && (
                <div className="pt-4">
                  <p className="text-xs font-medium text-slate-700 mb-2">Live preview</p>
                  <ClinicMap name={form.clinic_display_name} address={form.clinic_display_address} pincode={form.clinic_display_pincode} height={220} />
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-amber-800 mt-1">
              <Lock size={12} /> Upgrade to Premium to enable clinic branding with embedded map.
            </div>
          )}
        </div>

        {/* NARRATIVE BLOCKS — Premium+ */}
        {canEdit && (
          <div className="border border-sky-200 rounded-xl p-5 bg-sky-50/30 mt-5" data-testid="narrative-section">
            <p className="text-xs uppercase tracking-wider text-slate-600 font-semibold mb-1">Narrative Blocks</p>
            <p className="text-xs text-slate-500 mb-4">Your story and announcements — shown on your public website.</p>

            <label className="text-xs font-medium text-slate-700 block mb-1 mt-2">Why I chose an independent practice (optional)</label>
            <textarea rows={4} maxLength={1000}
              value={narrativeForm.why_independent}
              onChange={e => setNarrativeForm({ ...narrativeForm, why_independent: e.target.value })}
              placeholder="Write your own story — why you chose to practice independently, what kind of care you believe in. Renders as a first-person narrative block on your website. Max 1000 characters."
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
              data-testid="narrative-why-independent" />
            <p className="text-[10px] text-slate-400 mt-0.5 text-right">{narrativeForm.why_independent.length}/1000</p>

            <label className="text-xs font-medium text-slate-700 block mb-1 mt-4">Announcement banner (shown at top of public website)</label>
            <input type="text" maxLength={200}
              value={narrativeForm.clinic_announcement}
              onChange={e => setNarrativeForm({ ...narrativeForm, clinic_announcement: e.target.value })}
              placeholder='e.g. "Walk-ins welcome until 8 PM today" or "On leave from 12–16 May"'
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="narrative-announcement" />
            <div className="flex items-center gap-3 mt-2">
              <label className="text-[11px] font-medium text-slate-600">Expires in</label>
              <select
                value={narrativeForm.announcement_expires_hours}
                onChange={e => setNarrativeForm({ ...narrativeForm, announcement_expires_hours: parseInt(e.target.value, 10) })}
                className="text-xs px-2 py-1 rounded border border-slate-300 bg-white"
                data-testid="narrative-announcement-ttl">
                <option value="6">6 hours</option>
                <option value="24">24 hours</option>
                <option value="48">48 hours</option>
                <option value="168">7 days</option>
              </select>
              <span className="text-[10px] text-slate-400">Auto-hides after this window.</span>
            </div>

            {narrativeMsg.text && (
              <div className={`text-xs p-2 rounded-md mt-3 ${narrativeMsg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`} data-testid="narrative-msg">{narrativeMsg.text}</div>
            )}

            <button onClick={saveNarrative} disabled={savingNarrative}
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
              data-testid="narrative-save-btn">
              {savingNarrative ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save narrative
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

